import { numberFormat } from './utils';
import type { RatePresentationType } from './presentation.util';

// A missing/zero netPrice means we have no basis to compute a percentage at all — returning
// null (not 0) lets callers distinguish "we don't know the refund" from "the refund is 0%"
// (NPT-1565).
export function calculateRefundPercentage(netPrice: number, inclusiveRate: number): number | null {
   if (!netPrice) return null;
   return Math.round(((netPrice - inclusiveRate) / netPrice) * 100);
}

export function calculatePerNightDifference(basePublicRate: number, variantPublicRate: number, nightCount: number): number {
   if (!nightCount) return 0;
   return (basePublicRate - variantPublicRate) / nightCount;
}

export function calculateRefundAmount(publicRate: number, refundPercent: number): number {
   return (publicRate * refundPercent) / 100;
}

/**
 * Margin as a share of what the traveler pays (NPT-2197 A1, Clay, Oct 23 2026): a $100 net sold at
 * $125 is 20% margin. It replaced markup over net (25% for the same rate), which read higher.
 */
export function calculateMarginPercentage(publicRate: number, netRate: number): string {
   if (!publicRate || !netRate) return '0';
   return (((publicRate - netRate) / publicRate) * 100).toFixed(1);
}

// Derives the room-only total (excludes included tax and mandatory surcharges) directly
// from publicRate rather than multiplying the per-night nightlyRate back out — nightlyRate
// is already rounded to cents, so nightlyRate * nights drifts from the true total by a few
// cents over multi-night stays. Subtracting from publicRate avoids that compounding error.
//
// Returns 0 (not NaN) when publicRate hasn't loaded yet — React renders NaN as literal text,
// unlike null/undefined/0, so every call site used to need its own
// `publicRate ? calculateRoomPriceTotal(...) : undefined` guard. Guarding once here lets that
// per-call-site ternary go away: 0 is falsy everywhere this value is consumed (`value && ...`
// gates, `numberFormat`'s own `amount === 0` check), so a not-yet-loaded rate still renders
// nothing (NPT-1541 review finding 3).
export function calculateRoomPriceTotal(publicRate: number, includedTax: number, mandatoriesTotal?: number): number {
   if (!publicRate) return 0;
   return publicRate - (includedTax || 0) - (mandatoriesTotal || 0);
}

export function aggregateMandatorySurcharges(surcharges: Array<{ charge: string; rate?: { exclusive?: number } }>): number {
   return surcharges
      .filter((s) => s.charge === 'Mandatory')
      .reduce((acc, s) => acc + (Number(s?.rate?.exclusive) || 0), 0);
}

export function formatRefundDisplay(refundPercent: number | null, publicRate: number, currency: string): string {
   if (refundPercent == null) return 'Refund unavailable';
   if (refundPercent === 100) return 'Full Refund';
   if (refundPercent === 0) return 'No Refund';
   return numberFormat(calculateRefundAmount(publicRate, refundPercent), currency);
}

// nightlyRate from the API covers ALL rooms per night — break out the
// single-room rate so multi-room checkouts show "$100.00 x 5 rooms x 2 nights".
export function formatRoomPriceBreakdown(nightlyRate: number, rooms: number, nightNumber: number): string {
   const roomCount = rooms > 0 ? rooms : 1;
   const roomsPart = roomCount > 1 ? `${roomCount} rooms x ` : '';
   return `${numberFormat(nightlyRate / roomCount)} x ${roomsPart}${nightNumber} ${nightNumber === 1 ? 'night' : 'nights'}`;
}

// ── NPT-1557 three-way margin split ─────────────────────────────────────────
// The API's room commission object carries a realized `marginBreakdown` computed by ONE backend
// helper. Prices must read these values, never recompute the split client-side. Whether the
// traveler is TOLD about a saving is a separate question, answered by `commission.presentation`
// through presentation.util (NPT-2004): any org may pass a traveler share, so the discount below
// applies whatever the copy says.

export type MarginBreakdownType = {
   ourPrice: number;
   travelerDiscountAmount: number;
   savingsPct: number;
   /**
    * The discounted per-night rate. EVERY traveler-facing per-night surface must render this
    * rather than `commission.nightlyRate`, or "$X x N nights = $Y" stops reconciling — use
    * `getDisplayNightlyRate` rather than reading it directly.
    */
   ourNightlyRate: number;
   /** Admin-only; absent on traveler responses (the API strips them). */
   platformAmount?: number;
   /** Admin-only; absent on traveler responses (the API strips them). */
   orgProceedsAmount?: number;
};

export type CommissionWithBreakdownType = {
   marginBreakdown?: MarginBreakdownType;
   /** NPT-2004 — what the traveler is told about this rate; read it through `getRatePresentation`. */
   presentation?: RatePresentationType | null;
} & Record<string, any>;

/**
 * The traveler discount on a rate, or null when there is none. Price, not presentation: every
 * total and per-night figure applies it, and only `getRatePresentation(commission).showSavings`
 * decides whether a surface may SAY the traveler saved. All values come from the API's
 * marginBreakdown.
 */
export function getTravelerDiscount(commission?: CommissionWithBreakdownType | null): {
   travelerDiscountAmount: number;
   savingsPct: number;
   ourPrice: number;
   ourNightlyRate: number;
} | null {
   const travelerDiscountAmount = Number(commission?.marginBreakdown?.travelerDiscountAmount) || 0;
   if (travelerDiscountAmount <= 0) return null;
   return {
      travelerDiscountAmount,
      savingsPct: Number(commission?.marginBreakdown?.savingsPct) || 0,
      ourPrice: Number(commission?.marginBreakdown?.ourPrice) || 0,
      ourNightlyRate: Number(commission?.marginBreakdown?.ourNightlyRate) || 0,
   };
}

/**
 * The per-night rate for the WHOLE BOOKING: the API's discounted `ourNightlyRate` when the rate
 * carries a traveler discount, the plain `nightlyRate` otherwise.
 *
 * ALL ROOMS, not one. Booking Holdings prices whatever the search asked for and returns a single
 * `totalPayment` covering every room of it; `calculateCommission` divides that by the nights and
 * never by the rooms. So on a ten-room search this is what ten rooms cost for one night.
 *
 * That is the right quantity for a TOTAL and the wrong one to print beside the word "night"
 * (NPT-1718: the season planner quoted a hotel at $180 a night and the same hotel and stay, searched
 * at the club's block size, printed $1,800). Anything rendering a per-night figure to a traveler
 * wants `getPerRoomNightlyRate` instead; this stays for totals, for identities like
 * `rate * nights === tax-excluded total`, and for the admin breakdown that reports what the API
 * actually sent.
 *
 * Do NOT subtract the traveler discount at a call site — `nightlyRate` is computed by the API and
 * three call sites re-implementing the division is exactly the drift NPT-1557 exists to prevent
 * (review finding 5).
 */
export function getDisplayNightlyRate(commission?: CommissionWithBreakdownType | null): number | undefined {
   const discount = getTravelerDiscount(commission);
   if (discount) return discount.ourNightlyRate;
   const nightlyRate = commission?.nightlyRate;
   return nightlyRate == null ? undefined : Number(nightlyRate);
}

/**
 * Applies the traveler discount to a displayed total (e.g. the tax-excluded card total).
 * Nullish totals pass through unchanged (callers historically treat them as falsy/any).
 */
export function applyTravelerDiscount(total: number | null | undefined, commission?: CommissionWithBreakdownType | null): number {
   if (total == null) return total as unknown as number;
   const discount = getTravelerDiscount(commission);
   if (!discount) return total;
   return Math.max(total - discount.travelerDiscountAmount, 0);
}

// Same all-rooms -> single-room breakout as formatRoomPriceBreakdown, but returning
// the raw number for callers that need to format/compare it themselves (map markers,
// sidebar cards, budget filtering) rather than render a fixed breakdown string.
export function getRoomNightlyRate(nightlyRate: number | null | undefined, rooms?: number | string | null): number | undefined {
   if (nightlyRate == null) return undefined;
   const roomCount = Number(rooms) > 0 ? Number(rooms) : 1;
   return nightlyRate / roomCount;
}

/**
 * What ONE room costs per night — the figure that belongs next to the word "night".
 *
 * The pairing every per-night surface needs, and the one NPT-1718 was raised for: this helper
 * existed as two separate calls and only the map surfaces made both of them, so a map pin and the
 * result card beside it printed numbers ten times apart for the same hotel on the same stay.
 *
 * `rooms` is the room count the SEARCH asked for, because that is what the supplier priced. Pass
 * it from wherever the surface keeps it (the `rooms` query param, a planner game's `hotelSearch`,
 * a prop). A missing or nonsense value means one room, which is what every single-room surface
 * gets today and leaves those unchanged.
 */
export function getPerRoomNightlyRate(
   commission?: CommissionWithBreakdownType | null,
   rooms?: number | string | null,
): number | undefined {
   return getRoomNightlyRate(getDisplayNightlyRate(commission), rooms);
}

/**
 * The per-room nightly price a hotel RESULT shows, for anything in the list-item shape
 * (`item.rates[0].commission`): the number on its map pin and result card, and therefore also the
 * number the price filter, the price histogram and the price sorts must use. One helper so they can
 * never disagree; with a traveler discount that is the discounted `ourNightlyRate`, not the raw
 * `nightlyRate` (NPT-1932 review). Null when there is no usable price (loading, sold out, zero).
 */
export function getItemPerRoomNightlyRate(
   item: { rates?: Array<{ commission?: CommissionWithBreakdownType | null } | null | undefined> | null } | null | undefined,
   rooms?: number | string | null,
): number | null {
   const v = getPerRoomNightlyRate(item?.rates?.[0]?.commission, rooms);
   return v != null && Number.isFinite(v) && v > 0 ? v : null;
}

type RankableRate = {
   commission?: CommissionWithBreakdownType | null;
   totalPayment?: { tax?: number | null } | null;
} | null | undefined;

/**
 * What the traveler is actually charged for a rate: the discounted `ourPrice` when the rate carries
 * a traveler discount, the public rate otherwise. The same figure the room card's checkout total and checkout itself charge.
 * Undefined when the rate has no price yet.
 */
export function getTravelerChargeTotal(rate: RankableRate): number | undefined {
   const publicRate = Number(rate?.commission?.publicRate);
   if (!publicRate) return undefined;
   const discount = getTravelerDiscount(rate?.commission);
   return discount ? discount.ourPrice : publicRate;
}

/**
 * Orders two rates by what they cost the traveler, cheapest first. Rates without a price sort last.
 *
 * The basis is the CHARGE, tax included, not the tax-exclusive subtotal the cards print. Booking
 * Holdings often quotes several products of one room at the same public rate (its minSellPrice)
 * on different nets, and the included tax is computed on the net, so the product we earn least on
 * carries the most tax and the LOWEST subtotal. Ranking by subtotal picked exactly that product:
 * the traveler paid the same, the organization got less than half, and the hotel page and
 * checkout disagreed with the search card, which shows the product BH itself ranks first
 * (hotel 2454124, Nov 11-13 2026: card $49.46, page and checkout $21.07, both $500.24).
 * The subtotal could even rank a dearer charge first when its tax share was larger.
 *
 * Ties on the charge go to the larger proceeds (for the same price, the rate that gives the most),
 * then to the lower subtotal so the order stays deterministic.
 */
export function compareRatesForTraveler(a: RankableRate, b: RankableRate): number {
   const chargeA = getTravelerChargeTotal(a) ?? Infinity;
   const chargeB = getTravelerChargeTotal(b) ?? Infinity;
   if (chargeA !== chargeB) return chargeA < chargeB ? -1 : 1;
   const proceedsA = Number(a?.commission?.proceeds) || 0;
   const proceedsB = Number(b?.commission?.proceeds) || 0;
   if (proceedsA !== proceedsB) return proceedsB - proceedsA;
   const subtotal = (r: RankableRate) =>
      calculateRoomPriceTotal(Number(r?.commission?.publicRate), Number(r?.totalPayment?.tax), Number(r?.commission?.mandatoriesTotal));
   return subtotal(a) - subtotal(b);
}

/**
 * THE rule for choosing between rates: the one rate a surface should default to out of `rates`.
 * Every place that picks "the" rate for a room or hotel (the hotel page header and room cards, the
 * season planner's room cards) goes through this, so they cannot disagree with each other or with
 * checkout:
 *
 *   1. the lowest amount the traveler is charged (`getTravelerChargeTotal`, tax included);
 *   2. on a tie, the highest proceeds, so the same price raises the most for the organization;
 *   3. on a tie, the lower tax-exclusive subtotal, only to keep the choice deterministic.
 *
 * Rates without a price are never picked. Undefined when nothing has a price. The first of several
 * equal rates wins, so callers that pre-order (chosen room first) keep their order on a full tie.
 */
export function pickBestRate<T extends RankableRate>(rates: ReadonlyArray<T> | null | undefined): T | undefined {
   let best: T | undefined;
   for (const rate of rates ?? []) {
      if (getTravelerChargeTotal(rate) === undefined) continue;
      if (best === undefined || compareRatesForTraveler(rate, best) < 0) best = rate;
   }
   return best;
}

/**
 * The cheapest priced rate on the hotel page, by `pickBestRate`: the rate behind its header price,
 * its proceeds pill and the "Find a room" CTA. `rooms` may be grouped rooms (each with `.options`)
 * or flat rates; every option is considered, not just a group's representative, so a cheaper hidden
 * option is not missed. Undefined when nothing has a price.
 */
export function getCheapestRate<T extends NonNullable<RankableRate>>(rooms: Array<T & { options?: T[] }> | null | undefined): T | undefined {
   return pickBestRate((rooms ?? []).flatMap(room => (room?.options?.length ? room.options : [room])));
}
